import emptyFunction from 'react-utils/emptyFunction';
import { useCallback, useMemo } from 'react';
import PropertyInputV2 from 'customer-data-properties/input/PropertyInputV2';
import { shouldRightAlignProperty } from '../utils/shouldRightAlignProperty';
import { EditCellErrorText, EditCellFloatyWrapper, EditCellPropertyInputWrapper, EditCellContainer } from './StyledInlineEditCellComponents';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const alwaysUndefined = () => undefined;
const baseInputProps = {
  placeholder: '--',
  defaultOpen: true,
  buttonUse: 'unstyled',
  styled: false,
  'data-selenium-test': 'framework-data-table-editable-cell-input',
  disableFocusStyles: true
};
const InlineEditCell = ({
  objectTypeId,
  objectId,
  propertyDefinition,
  getSecondaryPropertyValue,
  disableEditMode,
  handleKeyDown,
  onPropertyUpdates = emptyFunction,
  onValidationChange = emptyFunction,
  value,
  validationResult,
  enableConditionalPropertiesPanel = false,
  enableConditionalPropertyOptions = false,
  enableDependentAssociations = false,
  enableAutosave,
  onAutosave,
  canEditValue = true,
  handleCompositionEnd
}) => {
  const handleOnChange = useCallback(updates => {
    onPropertyUpdates({
      objectTypeId,
      objectId,
      values: updates
    });
  }, [objectId, objectTypeId, onPropertyUpdates]);
  const inputProps = useMemo(() => Object.assign({}, baseInputProps, {
    style: shouldRightAlignProperty(propertyDefinition) ? {
      textAlign: 'right'
    } : {},
    // Detect when a user has ended a composition event (IME), i.e. for Japanese Kanji input.
    onCompositionEnd: handleCompositionEnd
  }), [handleCompositionEnd, propertyDefinition]);
  const onOpenChange = useCallback(({
    target: {
      value: open
    }
  }) => {
    if (!open && !enableConditionalPropertiesPanel) {
      disableEditMode({
        wasKeyboardExit: true
      });
    }
  }, [disableEditMode, enableConditionalPropertiesPanel]);
  const errorMessage = validationResult && validationResult.message;
  const additionalHelpMessage = validationResult && validationResult.additionalHelp;
  const errorMessageWithAdditionalHelp = additionalHelpMessage ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [errorMessage, " ", additionalHelpMessage]
  }) : errorMessage;
  const doesErrorExist = errorMessage !== undefined;
  const isFileProperty = isFile(propertyDefinition);
  return /*#__PURE__*/_jsxs(EditCellFloatyWrapper, {
    children: [/*#__PURE__*/_jsx(EditCellPropertyInputWrapper, {
      "aria-invalid": doesErrorExist,
      children: /*#__PURE__*/_jsx(EditCellContainer, {
        children: /*#__PURE__*/_jsx(PropertyInputV2, {
          use: "table-cell",
          inputProps: inputProps
          // HACK: onKeyDown doesn't propagate through date inputs, so we watch
          // onOpenChange as a proxy for the input closing
          ,
          onOpenChange: onOpenChange,
          autoFocus: true,
          objectId: objectId,
          objectTypeId: objectTypeId,
          property: propertyDefinition,
          value: value,
          onChange: handleOnChange,
          getDraftValue: getSecondaryPropertyValue,
          getSavedValue: alwaysUndefined,
          onValidationChange: onValidationChange,
          onKeyUp: handleKeyDown,
          enableConditionalPropertiesPanel: enableConditionalPropertiesPanel,
          enableConditionalPropertyOptions: enableConditionalPropertyOptions,
          enableDependentAssociations: enableDependentAssociations
          // acts as an extra layer of protection to prevent editing of a property
          ,
          readOnly: !canEditValue,
          inputMode: objectId ? 'edit' : 'create',
          enableAutosave: enableAutosave,
          onSave: onAutosave
        })
      })
    }), doesErrorExist && !isFileProperty && /*#__PURE__*/_jsx(EditCellErrorText, {
      children: errorMessageWithAdditionalHelp
    })]
  });
};
export default InlineEditCell;